import React from 'react';
import {
  Paper, Box, Typography, Grid, Chip, Button, IconButton,
  useMediaQuery, useTheme,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Receipt as ReceiptIcon } from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const BudgetOverview = ({ budget, project, filter, collapsed, onToggleCollapse, onFilterChange, userRole, onOpenInvoices }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!budget) return null;

  const chartData = [
    { name: 'Labor', value: parseFloat(budget.total_labor), color: '#2196f3' },
    { name: 'Materials', value: parseFloat(budget.total_materials), color: '#ff9800' },
    { name: 'Both', fullName: 'Materials and Labor', value: parseFloat(budget.total_both), color: '#9c27b0' },
  ];

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={collapsed ? 0 : 2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5">Budget Overview</Typography>
          {collapsed && (
            <>
              <Typography variant="h6" color="primary" fontWeight="bold">
                ${parseFloat(budget.total_cost).toLocaleString()}
              </Typography>
              <Chip label={`${budget.approved_items} Approved`} size="small" color="success" />
              <Chip label={`${budget.outstanding_items} Outstanding`} size="small" color="warning" />
              <Chip label={`${budget.rejected_items} Rejected`} size="small" color="error" />
            </>
          )}
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {!collapsed && (userRole === 'contractor' || userRole === 'admin') && (
            <Button variant="contained" startIcon={<ReceiptIcon />} onClick={onOpenInvoices} sx={{ mr: 2 }}>
              Invoices
            </Button>
          )}
          {!collapsed && (
            <>
              <Chip label="All" color={filter === 'all' ? 'primary' : 'default'} onClick={() => onFilterChange('all')} clickable />
              <Chip label="Approved" color={filter === 'approved' ? 'success' : 'default'} onClick={() => onFilterChange('approved')} clickable />
              <Chip label="Outstanding" color={filter === 'outstanding' ? 'warning' : 'default'} onClick={() => onFilterChange('outstanding')} clickable />
              <Chip label="Rejected" color={filter === 'rejected' ? 'error' : 'default'} onClick={() => onFilterChange('rejected')} clickable />
            </>
          )}
          <IconButton onClick={onToggleCollapse} aria-label={collapsed ? 'Expand budget overview' : 'Collapse budget overview'}>
            <ExpandMoreIcon sx={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)', transition: '0.3s' }} />
          </IconButton>
        </Box>
      </Box>
      {!collapsed && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box height={300} sx={{ position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="45%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: $${value.toLocaleString()}`}
                    outerRadius={isMobile ? 60 : 80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => {
                    const fullName = props.payload.fullName || name;
                    return [`$${value.toLocaleString()}`, fullName];
                  }} />
                  <Legend formatter={(value, entry) => entry.payload.fullName || value} wrapperStyle={{ paddingTop: '10px' }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              {(() => {
                const subtotal = parseFloat(budget.total_cost || 0);
                const markupRate = parseFloat(project?.markup_rate || 0);
                const taxRate = parseFloat(project?.tax_rate || 0);
                const markupAmt = subtotal * (markupRate / 100);
                const afterMarkup = subtotal + markupAmt;
                const taxAmt = afterMarkup * (taxRate / 100);
                const grandTotal = afterMarkup + taxAmt;
                // Markup is the contractor's margin — homeowners must never see
                // the breakdown that reveals it. They get the bottom-line price
                // (and optionally the tax line, since tax is standard on
                // estimates and they could compute it themselves anyway).
                const isContractorView = userRole === 'contractor' || userRole === 'admin';
                const showFullBreakdown = isContractorView && (markupRate > 0 || taxRate > 0);
                const showTaxOnlyToHomeowner = !isContractorView && taxRate > 0;
                return (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      ${grandTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Total Budget</Typography>
                    {showFullBreakdown && (
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                          <Typography variant="caption">${subtotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                        </Box>
                        {markupRate > 0 && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">Markup ({markupRate}%)</Typography>
                            <Typography variant="caption">${markupAmt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                          </Box>
                        )}
                        {taxRate > 0 && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">Tax ({taxRate}%)</Typography>
                            <Typography variant="caption">${taxAmt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                    {showTaxOnlyToHomeowner && (
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Includes tax ({taxRate}%)</Typography>
                          <Typography variant="caption">${taxAmt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                );
              })()}
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 } }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {budget.approved_items}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      Approved
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 } }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {budget.outstanding_items}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      Outstanding
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 } }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {budget.rejected_items}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      Rejected
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default BudgetOverview;
